<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import LicensePlateRender from '@/components/license-plate/LicensePlateRender.vue';
import LicensePlateRenderButton from '@/components/license-plate/LicensePlateRenderButton.vue';
import ChassisNumberRender from '@/components/chassis-number/ChassisNumberRender.vue';
import ChassisNumberRenderButton from '@/components/chassis-number/ChassisNumberRenderButton.vue';
import { Country } from '@/constants/country';
import type { VehicleIdentifierPlateSize } from '@/constants/vehicle-identifier-plate-size';
import {
  DEFAULT_VEHICLE_IDENTIFIER_SETTINGS,
  type VehicleIdentifierSettings,
} from '@/constants/vehicle-identifier';
import { resolveVehicleIdentifier } from '@/helpers/vehicle-identifier-helper';
import { useOrganizationStore } from '@/stores/organization/organization-store';

const props = defineProps({
  licensePlate: {
    type: String,
    required: false,
  },
  chassisNumber: {
    type: String,
    required: false,
  },
  country: {
    type: Number as PropType<Country>,
    required: false,
  },
  size: {
    type: String as PropType<VehicleIdentifierPlateSize>,
    default: 'medium',
    required: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
    required: false,
  },
  hasLinkToAssignment: {
    type: Boolean,
    default: false,
    required: false,
  },
  assignmentId: {
    type: Number,
    required: false,
  },
  hasLinkToVehicle: {
    type: Boolean,
    default: false,
    required: false,
  },
  vehicleId: {
    type: Number,
    required: false,
  },
});

const organizationStore = useOrganizationStore();

const settings = computed<VehicleIdentifierSettings>(() => ({
  primaryVehicleIdentifier:
    organizationStore.organizationSettings?.primaryVehicleIdentifier
    ?? DEFAULT_VEHICLE_IDENTIFIER_SETTINGS.primaryVehicleIdentifier,
  chassisNumberDisplayMode:
    organizationStore.organizationSettings?.chassisNumberDisplayMode
    ?? DEFAULT_VEHICLE_IDENTIFIER_SETTINGS.chassisNumberDisplayMode,
}));

const resolved = computed(() =>
  resolveVehicleIdentifier(
    {
      licensePlate: props.licensePlate,
      chassisNumber: props.chassisNumber,
      country: props.country,
    },
    settings.value,
  ),
);

const useButtonVariant = computed(
  () => props.hasLinkToAssignment || props.hasLinkToVehicle,
);
</script>

<template>
  <template v-if="resolved.kind === 'license-plate'">
    <license-plate-render-button
      v-if="useButtonVariant"
      :license-plate="resolved.value"
      :country="resolved.country"
      :size="props.size"
      :read-only="props.readOnly"
      :has-link-to-assignment="props.hasLinkToAssignment"
      :assignment-id="props.assignmentId"
      :has-link-to-vehicle="props.hasLinkToVehicle"
      :vehicle-id="props.vehicleId"
    />
    <license-plate-render
      v-else
      :license-plate="resolved.value"
      :country="resolved.country"
      :size="props.size"
      :read-only="props.readOnly"
    />
  </template>
  <template v-else-if="resolved.kind === 'chassis'">
    <chassis-number-render-button
      v-if="useButtonVariant"
      :chassis-number="resolved.value"
      :size="props.size"
      :read-only="props.readOnly"
      :truncate="resolved.truncated"
      :has-link-to-assignment="props.hasLinkToAssignment"
      :assignment-id="props.assignmentId"
      :has-link-to-vehicle="props.hasLinkToVehicle"
      :vehicle-id="props.vehicleId"
    />
    <chassis-number-render
      v-else
      :chassis-number="resolved.value"
      :size="props.size"
      :read-only="props.readOnly"
      :truncate="resolved.truncated"
    />
  </template>
</template>
